import { MSLatLng, MSPoint } from 'src/app/models/map.model';
import { CONSTANTS } from 'src/app/services/config.service';

type OffsetVector = {
  xOffset: number;
  yOffset: number;
};

//Offsets point by offset amount, along perpendicular of a line segment the point is either on or already perpendicular to
export function offsetLatLngAlongPerpendicular(
  startLatLng: MSLatLng | null,
  midLatLng: MSLatLng,
  endLatLng: MSLatLng | null,
  offset: number
): MSLatLng {
  const lineSegmentStart = startLatLng ? latLngToPoint(startLatLng) : latLngToPoint(midLatLng);
  const lineSegmentEnd = endLatLng ? latLngToPoint(endLatLng) : latLngToPoint(midLatLng);

  if (lineSegmentStart === lineSegmentEnd) {
    throw new Error('Cannot offset a line segment with zero length');
  }

  const midPoint = latLngToPoint(midLatLng);

  const perpendicularOffset = getPerpendicularOffset(lineSegmentStart, lineSegmentEnd, offset);
  const offsetMidPoint: MSPoint = getOffsetPoint(midPoint, perpendicularOffset);
  const offsetLatLng = pointToLatLng(offsetMidPoint);

  return offsetLatLng;
}

function getPerpendicularOffset(start: MSPoint, end: MSPoint, offset: number): OffsetVector {
  const lineX = end.x - start.x;
  const lineY = end.y - start.y;

  const length = Math.sqrt(Math.pow(lineX, 2) + Math.pow(lineY, 2));
  const offsetScalar = offset / length;

  const perpendicularOffsetVector: OffsetVector = { xOffset: lineY * offsetScalar, yOffset: -lineX * offsetScalar };
  return perpendicularOffsetVector;
}

function getOffsetPoint(point: MSPoint, offset: OffsetVector): MSPoint {
  const offsetPoint: MSPoint = {
    x: point.x + offset.xOffset,
    y: point.y + offset.yOffset
  };

  return offsetPoint;
}

function latLngToPoint(latLng: MSLatLng): MSPoint {
  const point: MSPoint = {
    x: lngToX(latLng.lng),
    y: latToY(latLng.lat)
  };

  return point;
}

function pointToLatLng(point: MSPoint): MSLatLng {
  const latLng: MSLatLng = {
    lat: yToLat(point.y),
    lng: xToLng(point.x)
  };

  return latLng;
}

function lngToX(lng: number): number {
  const x = CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_WIDTH * ((lng + 180) / 360);

  return x;
}

function latToY(lat: number): number {
  if (lat > 85.05113) {
    return CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT;
  } else if (lat < -85.05113) {
    return 0;
  }

  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT / 2 - (CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT * mercN) / (2 * Math.PI);

  return y;
}

function xToLng(x: number): number {
  const lng = (x * 360) / CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_WIDTH - 180;

  return lng;
}

function yToLat(y: number): number {
  const invMercN = ((CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT / 2 - y) * 2 * Math.PI) / CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT;
  const latRad = 2 * (Math.atan(Math.exp(invMercN)) - Math.PI / 4);
  const lat = (latRad * 180) / Math.PI;

  return lat;
}
